// components/umra-pdf-template/umra-pdf-template.component.ts
import { Component, Input } from '@angular/core';
import { UmraPackageProperties } from '../../Services/umra-packages.service';
import { AgencyMasterProperties } from '../../Models/AgencyMasterProperties';

@Component({
  selector: 'app-umra-pdf-template',
  templateUrl: './umra-pdf-template.component.html',
  styleUrls: ['./umra-pdf-template.component.css']
})
export class UmraPdfTemplateComponent {
  @Input() packages: UmraPackageProperties[] = [];
  @Input() filters: { airline?: string; route?: string; sort?: string } = {};
  @Input() agency: AgencyMasterProperties = new AgencyMasterProperties();

  todayDate: Date = new Date();
  get logo(): string {
    // 1. Bulletproof check: Ensure agency exists, the path exists, and it's actually a string
    if (!this.agency || !this.agency.logoFilePath || typeof this.agency.logoFilePath != 'string') {
      return '';
    }

    const fileData = this.agency.logoFilePath;

    // 2. Check if the backend already sent a complete Data URI
    if (fileData.startsWith('data:')) {
      return fileData;
    }

    // 3. Auto-detect MIME type using Base64 Magic Bytes
    let mimeType = 'image/jpeg'; // Safe default
    if (fileData.startsWith('iVBORw0KGgo')) {
      mimeType = 'image/png';
    } else if (fileData.startsWith('/9j/') || fileData.startsWith('AA')) {
      mimeType = 'image/jpeg';
    } else if (fileData.startsWith('R0lGOD')) {
      mimeType = 'image/gif';
    } else if (fileData.startsWith('UklGR')) {
      mimeType = 'image/webp';
    }

    // 4. Return properly formatted Data URI with the mandatory comma
    return `data:${mimeType};base64,${fileData}`;
  } 
  // Get unique travel dates for filename
  get travelDates(): string {
    const dates = [...new Set(this.packages.map(p => p.travelDate))];
    return dates.slice(0, 2).join('_').replace(/\//g, '-');
  }

  // Get date range for display
  get dateRange(): string {
    if (this.packages.length === 0) return '';
    const dates = this.packages.map(p => new Date(p.travelDate));
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    return `${this.formatShortDate(min)} - ${this.formatShortDate(max)}`;
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatShortDate(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0
    }).format(price);
  }

  // Group packages by routeStart for organized display
  get groupedByRouteStart(): { routeStart: string; airlines: { airline: string; packages: UmraPackageProperties[] }[] }[] {
    const routeGroups = new Map<string, Map<string, UmraPackageProperties[]>>();

    // Sort all packages by TravelDate first
    const sortedPackages = [...this.packages].sort((a, b) => {
      const dateA = new Date(a.travelDate).getTime();
      const dateB = new Date(b.travelDate).getTime();
      return dateA - dateB;
    });

    sortedPackages.forEach(pkg => {
      const routeKey = pkg.routeStart || 'Other';
      const airlineKey = pkg.airlineName || 'Other';

      if (!routeGroups.has(routeKey)) {
        routeGroups.set(routeKey, new Map());
      }

      const airlineMap = routeGroups.get(routeKey)!;
      if (!airlineMap.has(airlineKey)) {
        airlineMap.set(airlineKey, []);
      }

      airlineMap.get(airlineKey).push(pkg);
    });

    return Array.from(routeGroups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([routeStart, airlineMap]) => ({
        routeStart,
        airlines: Array.from(airlineMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([airline, packages]) => ({ airline, packages }))
      }));
  }
}
